import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ColumnDefinition, Empty, PageHeading } from 'barte-design-system';
import { usePostHog } from '@posthog/react';

import { TableSkeleton } from '@/modules/shared/components'
import { formatDateToBr } from '@/modules/shared/helpers';
import { TRACK_EVENTS } from '@payfac/constants'
import { useGetPendenciesList } from '@payfac/services';
import { PendenciesShow } from './components/show';

import styles from './list.module.css';

import type { IPendency } from '@payfac/types';

export const PendenciesList = () => {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const [page, setPage] = useState(1);
    const [selectedPendencyId, setSelectedPendencyId] = useState<number | string | null>(null);

    const { data, isLoading } = useGetPendenciesList({
        filters: { page, limit: 10 }
    });

    const pendencies = data?.data?.data || [];

    const columns: ColumnDefinition<IPendency>[] = [
        {
            key: 'context',
            title: t('payfac.pagePendenciesTableColumnSeller'),
            dataIndex: 'context',
            width: '15%',
            render: (value, record) => record?.context?.seller?.name || '-'
        },
        {
            key: 'description',
            title: t('payfac.pagePendenciesTableColumnDescription'),
            dataIndex: 'title',
            width: '45%'
        },
        {
            key: 'type',
            title: t('payfac.pagePendenciesTableColumnType'),
            dataIndex: 'category',
            width: '15%',
            render: () => (
                <div className={styles.tag}>{t('payfac.pagePendenciesTypeTaxas')}</div>
            )
        },
        {
            key: 'createdAt',
            title: t('payfac.pagePendenciesTableColumnCreatedAt'),
            dataIndex: 'createdAt',
            width: '25%',
            render: (value) => value ? formatDateToBr(`${value}`) : '-'
        }
    ];

    const showEmptyState = !isLoading && (pendencies?.length === 0 || data?.data?.pagination?.total === 0);
    const totalPages = data?.data?.pagination?.pages || 1;

    const handleCloseDrawer = () => {
        setSelectedPendencyId(null);
    };

    useEffect(() => {
        posthog.capture(TRACK_EVENTS.PENDENCIES.LIST_VIEWED);
    }, []);

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t('payfac.pagePendenciesTitle')} - Barte</title>
            </Helmet>

            <PageHeading title={t('payfac.pagePendenciesTitle')} />

            {showEmptyState && <Empty showButton={false} />}

            {!showEmptyState &&
                <TableSkeleton<IPendency>
                    isLoading={isLoading}
                    columns={columns}
                    data={pendencies}
                    rowKey={record => record.id}
                    pagination={totalPages > 1 ? {
                        currentPage: page,
                        totalPages,
                        onPageChange: setPage
                    } : undefined}
                    onRowClick={(record) => setSelectedPendencyId(record.id)}
                />}

            <PendenciesShow
                isOpen={!!selectedPendencyId}
                onClose={handleCloseDrawer}
                id={selectedPendencyId}
            />
        </div>
    );
};
